'use client';

import { createFollower, deleteFollower } from '@/domain/roadmap/api';
import { ROADMAP_FOLLOWERS_KEY, useRoadmapFollowers } from '@/domain/roadmap/hooks';
import { type RoadmapFollower } from '@/domain/roadmap/types';
import { useUsers } from '@/domain/user/hooks';
import { type UserBrief } from '@/domain/user/types';
import { useAuth } from '@/hooks/useAuth';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { TextField } from '@andritzot/metris-web-ui/inputs/textfield';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useState } from 'react';

import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

export interface FollowersFieldHandle {
  save: () => Promise<void>;
  reset: () => void;
  isDirty: () => boolean;
}

interface FollowersFieldProps {
  roadmapId: number;
  viewOnly: boolean;
}

type FollowerEntry = UserBrief | string;

const getEntryEmail = (entry: FollowerEntry): string => (typeof entry === 'string' ? entry : entry?.email ?? '');

const getEntryLabel = (entry: FollowerEntry): string => (typeof entry === 'string' ? entry : entry.name);

const computeAddedEntries = (pending: FollowerEntry[], server: FollowerEntry[]) => {
  const serverEmails = new Set(server.map(getEntryEmail));
  return pending.filter((e) => !serverEmails.has(getEntryEmail(e)));
};

const computeRemovedEntries = (pending: FollowerEntry[], server: FollowerEntry[]) => {
  const pendingEmails = new Set(pending.map(getEntryEmail));
  return server.filter((e) => !pendingEmails.has(getEntryEmail(e)));
};

const buildRemoveOps = (removed: FollowerEntry[], followers: RoadmapFollower[]) =>
  removed.map((entry) => {
    const match = followers.find((f) => f.email === getEntryEmail(entry));
    return match ? deleteFollower(match.idRoadmapFollower) : Promise.resolve();
  });

export const FollowersField = forwardRef<FollowersFieldHandle, FollowersFieldProps>(function FollowersField(
  { roadmapId, viewOnly },
  ref,
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: users = [] } = useUsers({ enabled: !!user });
  const { data: followers = [] } = useRoadmapFollowers(roadmapId, !!roadmapId);

  const serverEntries: FollowerEntry[] = followers.map((f) => users.find((u) => u.email === f.email) ?? f.email);

  const [pendingEntries, setPendingEntries] = useState<FollowerEntry[] | null>(null);
  const selectedEntries = pendingEntries ?? serverEntries;

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (pendingEntries === null) return;

      const added = computeAddedEntries(pendingEntries, serverEntries);
      const removed = computeRemovedEntries(pendingEntries, serverEntries);

      const addOps = added.map((entry) =>
        createFollower({
          idRoadmap: roadmapId,
          follower: {
            name: getEntryLabel(entry),
            email: getEntryEmail(entry),
            idRoadmap: roadmapId,
            idRoadmapFollower: 0,
          },
        }),
      );
      const removeOps = buildRemoveOps(removed, followers);

      await Promise.all([...addOps, ...removeOps]);
      await queryClient.invalidateQueries({ queryKey: ['roadmap', ROADMAP_FOLLOWERS_KEY, roadmapId] });
      setPendingEntries(null);
    },
    reset: () => setPendingEntries(null),
    isDirty: () => pendingEntries !== null,
  }));

  return (
    <Box sx={{ py: 0.75 }}>
      <FieldActionsWrapper>
        <Autocomplete<UserBrief, true, false, true>
          multiple
          freeSolo
          value={selectedEntries}
          onChange={(_, newValue) => setPendingEntries(newValue.map((v) => (typeof v === 'string' ? v : v)))}
          options={users}
          getOptionLabel={(opt) => getEntryEmail(opt)}
          getOptionKey={(opt) => (typeof opt === 'string' ? opt : opt.idUser)}
          isOptionEqualToValue={(opt, v) => getEntryEmail(opt) === getEntryEmail(v)}
          renderOption={(props, opt) => (
            <Box component="li" {...props}>
              <Box>
                <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                  {typeof opt === 'string' ? opt : opt.name}
                </Box>
                {typeof opt !== 'string' && (
                  <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}>
                    {opt.email}
                  </Box>
                )}
              </Box>
            </Box>
          )}
          label="Followers"
          placeholder="Type email or search users..."
          disabled={viewOnly}
          fullWidth
          size="small"
          renderInput={(params) => <TextField {...params} label="Followers" multiline minRows={3} />}
        />
      </FieldActionsWrapper>
    </Box>
  );
});
